import { ArtifactType } from '@app/client/common/structs/nft/artifacts';

// display types
export type ArtifactMintDisplayItem = SingleDisplayItem & ArtifactBreakdown & ArtifactMintData & { artifactId: string };
export type UserArtifactOwnedItems = {
    [key in ArtifactType]?: string[];
};

export type ArtifactTypeDisplay = {
    [key in ArtifactType]?: SingleDisplayItem;
};
export interface SingleDisplayItem {
    id: string;
    name?: string;
    image: string;
    thumbnail_image: string;
    icon_path?: string;
    animation_url: string;
    artifact_type: ArtifactType;
    lootbox_token_id_mapping?: LootBoxTokenIdMapping;
    artifact_type_id: string;
    external_link?: string;
    description?: string;
    attributes?: string[];
    owner_name?: string;
    token_id: string;
}

export type ArtifactMintDetails = {
    [key in ArtifactType]?: ArtifactMintData;
};

interface ArtifactMintData {
    lootbox_token_id_mapping: LootBoxTokenIdMapping;
    eligible_for_minting: string[];
    minting_enabled: boolean;
    owned_on_chain: string[];
    contract_address: string;
    token_id: string | null;
    used_on_chain: string[];
    burn_address?: string;
    fungible: boolean;
    owned: string[];
    used: string[];
    mintedIds: string[];
}

// display types end

// maps
export const artifactKeyToUrlMap = new Map<ArtifactType, string>([
    [ArtifactType.PrismCase, 'prism-case'],
    [ArtifactType.PrismKey, 'prism-key'],
    [ArtifactType.CloudSeed, 'cloud-seed'],
    [ArtifactType.CelestialRose, 'celestial-rose'],
]);
export const artifactUrlToKeyMap = new Map<string, ArtifactType>([
    ['prism-case', ArtifactType.PrismCase],
    ['prism-key', ArtifactType.PrismKey],
    ['cloud-seed', ArtifactType.CloudSeed],
    ['celestial-rose', ArtifactType.CelestialRose],
]);
// maps end

// response types
export type ArtifactTokenId = number;
export type LootBoxId = string;
export type LootBoxTokenIdMapping = {
    LootBoxId?: ArtifactTokenId;
};
export type ArtifactsResponse = Artifact[];

interface Artifact {
    required_artifact_state: string | null;
    artifact_type: ArtifactType;
    contract_address: string;
    minting_enabled: boolean;
    burn_address: string;
    fungible: boolean;
    token_id: null;

    total: number;
    consumed_total: number;
    available_total: number;
}

export type ArtifactBreakdownResponse = {
    [key in ArtifactType]?: ArtifactBreakdown;
};

interface ArtifactBreakdown {
    lootbox_token_id_mapping: LootBoxTokenIdMapping | null;
    eligible_to_request_opening: string[];
    request_opening_pending: string[];
    eligible_for_minting: string[];
    owned_on_chain: string[];
    used_on_chain: string[];
    owned: string[];
    used: string[];
    total: number; // total assets created
    consumed_total: number; // total assets with an owner (used/bought)
    available_total: number; // total asset w no owner
}
// response types end

//counts
export type ArtifactCount = {
    [key in ArtifactType]?: Counts;
};

interface Counts {
    total: number;
    consumed_total: number;
    available_total: number;
    remaining_supply?: number;
}

const DEFAULT_COUNTS = {
    total: 0,
    consumed_total: 0,
    available_total: 0,
};

export const DEFAULT_ARTIFACT_COUNT = {
    [ArtifactType.PrismCase]: DEFAULT_COUNTS,
    [ArtifactType.PrismKey]: DEFAULT_COUNTS,
    [ArtifactType.CloudSeed]: DEFAULT_COUNTS,
    [ArtifactType.CelestialRose]: DEFAULT_COUNTS,
};
// counts end

// artifact keys
const DEFAULT_KEYS = [
    ArtifactType.PrismCase,
    ArtifactType.PrismKey,
    ArtifactType.CloudSeed,
    ArtifactType.CelestialRose,
];

export const DEFAULT_KEYS_STATE = {
    uncollectedKeys: DEFAULT_KEYS,
    collectedKeys: {},
    totalKeys: [],
};

export interface ArtifactKeys {
    uncollectedKeys: ArtifactType[];
    collectedKeys: CollectedKeys;
    totalKeys: ArtifactType[];
}
type CollectedKeys = {
    [key in ArtifactType]?: Counts;
};
// artifact keys end
